import { LayoutHeader } from "@/Header";
import { Tabs } from "@/Tabs";
import { useTab } from "^/common/useTab";
import { createFileRoute, useRouter } from "@tanstack/react-router";
// import pet1Src from "$/images/pet1.png";
// import arrowSrc from "$/arrow-r.svg";

import NotificationCard from "@/NotificationCard";
import FloatingButton from "@/FloatingButton";
import { IconWrite } from "@/Icon/Wirite";
import useNotice, { type NoticeContentType } from "^/customer/notice";
import { useUserReserveNoticeRead } from "~/services/customer/reservation";
import { ReactNode, useEffect, useRef, useState } from "react";
import { IconCalendar } from "@/Icon/Calendar";
import { IconGallery } from "@/Icon/Gallery";
import { IconNotice } from "@/Icon/Notice";
import { IconInform } from "@/Icon/Inform";
import { IconSchool } from "@/Icon/School";
// import { IconNotify } from "@/Icon/Notify";
import { useLoadingStore } from "../../../stores/loading";
import { useInformListStore } from "../../../stores/informList";
import { useUserNoticeHasNewReply } from "../../../api/services/customer/notice";
// [성능 개선] VideoThumbnail 비활성화 - Canvas 기반 썸네일 생성이 메모리 과다 사용 (~32MB/동영상)
// 서버측 썸네일 생성 구현 전까지 플레이스홀더로 대체
// import { VideoThumbnail } from "@/common/VideoThumbnail";
export const Route = createFileRoute("/customer/layout/inform")({
  component: Notify,
});

// "2026-07-09 14:30:00" → "오늘" / "어제" / "7월 9일" / "2025년 12월 31일"
function formatDateLabel(date: string) {
  if (!date) return "";
  const [y, m, d] = date.split("-").map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(y, (m ?? 1) - 1, d ?? 1);
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);
  if (diffDays === 0) return "오늘";
  if (diffDays === 1) return "어제";
  if (y === now.getFullYear()) return `${m}월 ${d}일`;
  return `${y}년 ${m}월 ${d}일`;
}

// "2026-07-09 14:30:00" → "14:30"
function formatTime(dt?: string) {
  const time = (dt ?? "").split(/[T ]/)[1] ?? "";
  return time.slice(0, 5);
}

interface TimelineListProps {
  items: NoticeContentType[];
  renderItem: (item: NoticeContentType) => ReactNode;
}

// 알림 일시 — 응답 필드는 dtSend (구버전 호환으로 dtRegist 폴백)
function getNotiDateTime(item: NoticeContentType) {
  return item.dtSend ?? item.dtRegist ?? "";
}

/** 날짜별 그룹 + 좌측 세로선 타임라인. 점 색: 읽지 않음=주황, 읽음=회색 */
function TimelineList({ items, renderItem }: TimelineListProps) {
  const groups: { date: string; items: NoticeContentType[] }[] = [];
  for (const item of items) {
    const date = getNotiDateTime(item).split(/[T ]/)[0];
    const last = groups[groups.length - 1];
    if (last && last.date === date) {
      last.items.push(item);
    } else {
      groups.push({ date, items: [item] });
    }
  }

  return (
    <div className="px-1 pt-2 pb-1">
      <div className="relative ml-2 pl-4">
        {/* 그룹(날짜) 사이에서 끊기지 않는 연속 세로선 — 첫 날짜 칩 높이(top-6)부터 시작해
            첫 라벨 위로는 선이 보이지 않고, 이후 날짜 칩은 흰 배경으로 선 위에 얹힘 */}
        <span className="absolute bottom-2 left-0 top-6 w-[2px] rounded-full bg-[#E5E5E5]" />
        {groups.map((group) => (
          <div key={group.date || "unknown"}>
            {/* 날짜 구분 — 상하 여백 py-3 으로 모든 그룹 동일, 선보다 왼쪽으로 돌출 */}
            <div className="relative z-10 -ml-6 py-3">
              <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-bold shadow-sm">
                {formatDateLabel(group.date)}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {group.items.map((item, i) => (
                <div
                  key={item.notiRecvSeq ?? i}
                  data-noti-seq={item.notiRecvSeq}
                  className="relative"
                >
                  <span
                    className={`absolute left-[-20px] top-4 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                      item.readYn === "N" ? "bg-[#FF9934]" : "bg-[#D9D9D9]"
                    }`}
                  />
                  <div className="rounded-xl bg-white p-3 shadow-sm">
                    {renderItem(item)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Notify() {
  // 상세에서 뒤로 돌아온 경우에만 스냅샷 복원 (일반 진입은 새로 조회)
  const [restored] = useState(() => {
    const s = useInformListStore.getState();
    return s.returning && s.snapshot ? s.snapshot : null;
  });

  const { tab, handleTab, tabList } = useTab(
    ["받은 알림장", "보낸 알림장"],
    restored?.tab
  );

  // 복원 플래그 소비 (1회성 — 이후 일반 진입은 새로 조회)
  useEffect(() => {
    useInformListStore.getState().consume();
  }, []);

  // 새로운 댓글 상태 확인 API 호출
  const { data: newReplyData, refetch } = useUserNoticeHasNewReply();

  // 새 댓글 여부 API: 탭 변경 시 재호출
  useEffect(() => {
    refetch();
  }, [tab, refetch]);

  // 새로운 댓글 표시 플래그 생성
  const newReplyFlags = [
    newReplyData?.data?.hasRecipientNewReply || false, // 받은 알림장
    newReplyData?.data?.hasSenderNewReply || false, // 보낸 알림장
  ];


  const {
    noticeSenderList,
    noticeReceiverList,
    handlePage,
    pageSender,
    pageReceiver,
    isSenderLast,
    isReceiverLast,
    isLoading,
    isFetching,
  } = useNotice(restored ?? undefined);

  const { history } = useRouter();
  const reserveNoticeReadMutation = useUserReserveNoticeRead();

  // 무한 스크롤 — 하단 센티널이 보이면 다음 페이지 로드 (비즈니스 알림장과 동일 패턴)
  const receiverLoadMoreRef = useRef<HTMLDivElement>(null);
  const senderLoadMoreRef = useRef<HTMLDivElement>(null);

  // 로딩 스토어 사용
  const { toggleLoading } = useLoadingStore();

  // React Query 로딩 상태와 로딩 스토어 연동
  useEffect(() => {
    toggleLoading(isLoading || isFetching);
  }, [isLoading, isFetching, toggleLoading]);

  // 스크롤 복원: 재조회된 리스트가 실제로 그려진 뒤에 저장된 위치로 부드럽게 스크롤
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (!restored || scrollRestoredRef.current) return;
    const targetLength =
      restored.tab === "보낸 알림장"
        ? noticeSenderList.length
        : noticeReceiverList.length;
    if (targetLength === 0) return; // 아직 데이터 없음 — 그려진 뒤에 실행
    scrollRestoredRef.current = true;
    const main = document.getElementById("main");
    if (!main) return;
    // 렌더 완료 후 스크롤 (스르륵 이동)
    requestAnimationFrame(() => {
      // 클릭했던 카드가 있으면 그 카드가 화면 중앙에 오도록 (새 알림 유입으로
      // 픽셀 위치가 밀려도 정확). 없으면 저장된 픽셀 위치로 폴백.
      // ※ scrollIntoView 는 상위 스크롤 조상(문서/카드)까지 움직여 헤더가 밀리므로
      //    main 컨테이너의 scrollTop 만 직접 계산해서 이동한다
      const anchor = restored.anchorSeq
        ? main.querySelector<HTMLElement>(
            `[data-noti-seq="${restored.anchorSeq}"]`
          )
        : null;
      if (anchor) {
        const anchorRect = anchor.getBoundingClientRect();
        const mainRect = main.getBoundingClientRect();
        const target =
          main.scrollTop +
          (anchorRect.top - mainRect.top) -
          (main.clientHeight - anchorRect.height) / 2;
        main.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
      } else {
        main.scrollTo({ top: restored.scrollY, behavior: "smooth" });
      }
    });
  }, [restored, noticeReceiverList, noticeSenderList]);

  useEffect(() => {
    if (isReceiverLast || isLoading || isFetching) return;
    const el = receiverLoadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handlePage("받은 알림장");
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isReceiverLast, isLoading, isFetching, handlePage]);

  useEffect(() => {
    if (isSenderLast || isLoading || isFetching) return;
    const el = senderLoadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handlePage("보낸 알림장");
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSenderLast, isLoading, isFetching, handlePage]);


  // 상세로 이동하기 직전 현재 목록 상태 저장 — 뒤로가기 시 탭·목록·스크롤 복원용.
  // anchorSeq(클릭한 알림)를 저장해 복원 시 해당 카드 기준으로 스크롤 (픽셀 위치는 폴백)
  const saveSnapshot = (anchorSeq?: number) => {
    const main = document.getElementById("main");
    useInformListStore.getState().save({
      tab,
      scrollY: main?.scrollTop ?? 0,
      anchorSeq: anchorSeq ?? null,
      pageReceiver,
      pageSender,
    });
  };

  const wiriteButtonClick = () => {
    saveSnapshot();
    history.push("/customer/layout/inform-send");
  };
  const handleRead = (
    notiRecvSeq: number,
    cdNotiType: string,
    send: "N" | "Y",
    petSeq: number
  ) => {
    const noDetailTypes = [
      "NOTI_RESERV_CONFIRM",
      "NOTI_RESERV_CANCEL",
      "NOTI_ATTEND_STATUS_DISMISSED",
      "NOTI_ATTEND_STATUS_ATTENDED",
    ];
    if (!noDetailTypes.some((type) => cdNotiType.includes(type))) {
      saveSnapshot(notiRecvSeq);
      history.push(
        `/customer/layout/inform-detail?seq=${notiRecvSeq}&send=${send}&petSeq=${petSeq}`
      );
    }
  };

  return (
    <>
      <LayoutHeader title={tab} hasBackBtn={false} hasFootPrint={false} />
      <div className="p-2 flex-auto bg-[#F5F5F5]">
        <Tabs
          tabList={tabList}
          handleTab={handleTab}
          tab={tab}
          newReplyFlags={newReplyFlags}
        >
          <div className="flex-auto p-2">
            <TimelineList
              items={noticeReceiverList}
              renderItem={(item) => (
                <Card
                  title={item.notiTitle}
                  time={formatTime(getNotiDateTime(item))}
                  userNewReplyYn={item.userNewReplyYn}
                  cdNotiType={item.cdNotiType}
                  readYn={item.readYn}
                  onClick={() =>
                    handleRead(
                      item.notiRecvSeq,
                      item.cdNotiType,
                      "N",
                      item.petSeq
                    )
                  }
                >
                  <NotificationCard
                    text={item.notiContents}
                    className="min-w-0 whitespace-pre-wrap line-clamp-3"
                  >
                    <p
                      className={
                        "min-w-0 text-sm text-gray-500 whitespace-pre-wrap line-clamp-3"
                      }
                    >
                      {item.notiContents}
                    </p>
                    {/* 이미지 표시 */}
                    {((item.files && item.files.length > 0) ||
                      (item.fileUrls && item.fileUrls.length > 0)) && (
                      <div className="flex overflow-x-auto gap-1 mt-2">
                        {(item.files && item.files.length > 0
                          ? item.files.map((file) => ({
                              fileUrl: file.fileUrl || "",
                              thumbnailUrl: file.thumbnailUrl || null,
                              thumbnailStatus: file.thumbnailStatus || null,
                            }))
                          : (item.fileUrls || []).map((fileUrl) => ({
                              fileUrl,
                              thumbnailUrl: null,
                              thumbnailStatus: null,
                            }))
                        ).map((file, index) => {
                          const videoExtensions = [
                            ".mp4",
                            ".mov",
                            ".avi",
                            ".webm",
                            ".mkv",
                            ".m4v",
                            ".3gp",
                          ];
                          const isVideo = videoExtensions.some((ext) =>
                            file.fileUrl.toLowerCase().includes(ext)
                          );
                          const isThumbnailReady =
                            file.thumbnailStatus === "COMPLETE" &&
                            !!file.thumbnailUrl;
                          const displayUrl = isThumbnailReady
                            ? file.thumbnailUrl!
                            : file.fileUrl;
                          return (
                            <div
                              key={index}
                              className="relative flex-shrink-0 w-14 h-14"
                            >
                              {isVideo ? (
                                <>
                                  {isThumbnailReady ? (
                                    <img
                                      src={displayUrl}
                                      alt={`알림 동영상 썸네일 ${index + 1}`}
                                      className="object-cover w-full h-full rounded-md"
                                    />
                                  ) : (
                                    <div className="flex justify-center items-center w-full h-full bg-gray-300 rounded-md">
                                      <svg
                                        className="w-6 h-6 text-gray-500"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-md pointer-events-none">
                                    <svg
                                      className="w-4 h-4 text-white opacity-80"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                </>
                              ) : (
                                <img
                                  src={displayUrl}
                                  alt={`알림 이미지 ${index + 1}`}
                                  className="object-cover w-full h-full rounded-md"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(item.cdNotiType.includes("NOTI_RESERV_CONFIRM") ||
                      item.cdNotiType.includes("NOTI_RESERV_CANCEL")) && (
                      // 취소 알림에서도 예약했던 날짜·시간을 확인할 수 있도록 상세로 이동
                      <button
                        className="px-4 py-2 mt-2 rounded border border-yellow"
                        onClick={() => {
                          saveSnapshot(item.notiRecvSeq);
                          // notiRecvSeq가 있는 경우에만 API 호출
                          if (item.notiRecvSeq) {
                            reserveNoticeReadMutation.mutate(
                              { notiRecvSeq: item.notiRecvSeq },
                              {
                                onSuccess: () => {
                                  // API 호출 성공 후 페이지 이동
                                  history.push(
                                    `/customer/layout/calendar-reservation-chg?cdReservStatus=${item.cdReservStatus}&cdReservType=${item.cdReservType}&reservationSeq=${item.reservationSeq}&companyName=${item.companyName}`
                                  );
                                },
                                onError: (error) => {
                                  console.error("알림 읽음 처리 실패:", error);
                                  // API 실패해도 페이지 이동
                                  history.push(
                                    `/customer/layout/calendar-reservation-chg?cdReservStatus=${item.cdReservStatus}&cdReservType=${item.cdReservType}&reservationSeq=${item.reservationSeq}&companyName=${item.companyName}`
                                  );
                                },
                              }
                            );
                          } else {
                            // notiRecvSeq가 없는 경우 바로 페이지 이동
                            history.push(
                              `/customer/layout/calendar-reservation-chg?cdReservStatus=${item.cdReservStatus}&cdReservType=${item.cdReservType}&reservationSeq=${item.reservationSeq}&companyName=${item.companyName}`
                            );
                          }
                        }}
                      >
                        <p className="text-sm text-yellow">예약 정보 보기</p>
                      </button>
                    )}
                  </NotificationCard>
                </Card>
              )}
            />
            {/* 무한 스크롤 센티널 — 마지막 페이지면 렌더링하지 않음 */}
            {!isReceiverLast && <div ref={receiverLoadMoreRef} className="h-10" />}
          </div>
          <div className="flex-auto p-2">
            {noticeSenderList.length == 0 && (
              <div className="px-1 pt-4 text-sm text-gray-500">
                보낸 알림장이 없습니다.
              </div>
            )}
            <TimelineList
              items={noticeSenderList}
              renderItem={(item) => (
                <Card
                  title={item.notiTitle}
                  time={formatTime(getNotiDateTime(item))}
                  userNewReplyYn={item.userNewReplyYn}
                  cdNotiType={item.cdNotiType}
                  readYn={item.readYn}
                  onClick={() =>
                    handleRead(
                      item.notiRecvSeq,
                      item.cdNotiType,
                      "Y",
                      item.petSeq
                    )
                  }
                >
                  <NotificationCard
                    text={item.notiContents}
                    className="min-w-0 whitespace-pre-wrap line-clamp-3"
                  >
                    <p
                      className={
                        "min-w-0 text-sm text-gray-500 whitespace-pre-wrap line-clamp-3"
                      }
                    >
                      {item.notiContents}
                    </p>
                  </NotificationCard>
                </Card>
              )}
            />
            {/* 무한 스크롤 센티널 — 마지막 페이지면 렌더링하지 않음 */}
            {!isSenderLast && <div ref={senderLoadMoreRef} className="h-10" />}
            <FloatingButton onClick={() => wiriteButtonClick()}>
              <IconWrite />
            </FloatingButton>
          </div>
        </Tabs>
      </div>

    </>
  );
}

interface CardProps extends IconProps {
  title?: string;
  time?: string;
  children?: ReactNode;
  onClick?: () => void;
  userNewReplyYn: "Y" | "N";
}
interface IconProps {
  cdNotiType: string;
  readYn: "Y" | "N";
}

/** 알림 유형별 아이콘 — 비즈니스 알림장(NoticeTimeline) 배지와 동일한 매핑 */
const Icon = ({ cdNotiType, readYn }: IconProps) => {
  const active = readYn === "N";
  if (cdNotiType.includes("NOTI_DIARY")) return <IconInform active={active} />;
  if (cdNotiType.includes("NOTI_SCHEDULE"))
    return <IconCalendar active={active} />;
  if (cdNotiType.includes("NOTI_ANNOUNCEMENT"))
    return <IconNotice active={active} />;
  if (cdNotiType.includes("NOTI_PHOTO_ALBUM"))
    return <IconGallery active={active} />;
  if (cdNotiType.includes("NOTI_RESERV"))
    return <IconCalendar active={active} />;
  if (cdNotiType.includes("NOTI_ATTEND"))
    return <IconSchool active={active} />;
  return <IconNotice active={active} />;
};

const Card = ({
  children,
  title,
  time,
  onClick,
  userNewReplyYn,
  ...iconProps
}: CardProps) => {
  return (
    <div className="w-full" onClick={onClick}>
      <p className="flex gap-2 items-center">
        <Icon {...iconProps} />
        <span className="min-w-0 truncate">{title}</span>
        {userNewReplyYn === "Y" && (
          <div className="flex justify-center items-center w-6 h-4 text-xs font-bold text-[#ff9934] bg-red-500 rounded shrink-0">
            new
          </div>
        )}
        {time && (
          <span className="ml-auto shrink-0 text-xs text-gray-400">{time}</span>
        )}
      </p>
      {children ?? null}
    </div>
  );
};
